import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Camera, Clock, Crown, LogOut, Settings, Users, Baby, Plus, UtensilsCrossed, Heart } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';
import StreakBanner from '../components/StreakBanner';
import ProfileSwitcher from '../components/ProfileSwitcher';
import StreakReminder from '../components/StreakReminder';
import { checkStreakReminder } from '../components/PushNotifications';

// Pretty labels for stored answer values
const LABELS = {
  goal: {
    lose_weight: 'Lose weight',
    build_muscle: 'Build muscle',
    eat_cleaner: 'Eat cleaner',
    manage_condition: 'Manage condition',
    feel_better: 'Feel better'
  },
  eating_style: {
    keto: 'Keto',
    vegan: 'Vegan',
    vegetarian: 'Vegetarian',
    paleo: 'Paleo',
    gluten_free: 'Gluten-free',
    none: 'No diet'
  },
  shopping_for: {
    just_me: 'Just me',
    partner: 'Me & partner',
    family: 'Family',
    kids: 'Kids',
    elderly: 'Elderly parent'
  },
  avoid_ingredients: {
    added_sugar: 'Added sugar',
    artificial_dyes: 'Dyes',
    hfcs: 'HFCS',
    msg: 'MSG',
    preservatives: 'Preservatives',
    all_of_above: 'All processed'
  },
  allergies: {
    nuts: 'Nuts',
    dairy: 'Dairy',
    gluten: 'Gluten',
    soy: 'Soy',
    eggs: 'Eggs',
    shellfish: 'Shellfish',
    none: 'None'
  }
};

const MEDICAL_LABELS_HOME = {
  liquid: 'Liquid diet',
  pre_surgery: 'Pre-surgery',
  post_surgery: 'Post-surgery',
  glp1: 'GLP-1',
  diabetic: 'Diabetic',
  other: 'Medical'
};

const GLP1_MED_LABELS = {
  semaglutide: 'Semaglutide',
  tirzepatide: 'Tirzepatide',
  orforglipron: 'Orforglipron',
  liraglutide: 'Liraglutide',
  dulaglutide: 'Dulaglutide',
  not_sure: 'GLP-1'
};

const goalReminders = {
  lose_weight: 'Eyes on the prize — leaner choices add up.',
  build_muscle: 'Protein-forward picks keep gains rolling.',
  eat_cleaner: 'Fewer ingredients usually wins.',
  manage_condition: 'Consistency protects your health.',
  feel_better: 'Small swaps, big mood shifts.'
};

export default function Home() {
  const { token, logout, loadProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [profileList, setProfileList] = useState([]);
  const [activeProfileId, setActiveProfileId] = useState(null);
  const [switching, setSwitching] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const loadUserProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/profile/me`, {
        withCredentials: true
      });
      setProfile(response.data);
      setActiveProfileId(response.data.active_profile_id || response.data.profile_id || null);
    } catch (error) {
      console.error('[Home] Failed to load profile:', error);
    }
  }, []);

  const loadProfileList = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/profiles`, {
        withCredentials: true
      });
      setProfileList(r.data.profiles || []);
      if (r.data.active_profile_id) setActiveProfileId(r.data.active_profile_id);
    } catch (e) {
      console.warn('[Home] Failed to load profile list, switcher hidden:', e);
    }
  }, []);

  const loadRecentScans = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/scan/history`, {
        withCredentials: true
      });
      setRecentScans(response.data);
    } catch (error) {
      console.error('[Home] Failed to load recent scans:', error);
    }
  }, []);

  const switchProfile = useCallback(async (id) => {
    if (id === activeProfileId || switching) return;
    if (navigator.vibrate) navigator.vibrate(30);
    setSwitching(true);
    try {
      await axios.post(`${API_URL}/profiles/${id}/activate`, {}, { withCredentials: true });
      setActiveProfileId(id);
      await loadUserProfile();
      await loadRecentScans();
      toast.success('Switched profile');
    } catch (e) {
      console.error('[Home] Failed to switch profile:', e);
      toast.error('Could not switch');
    } finally {
      setSwitching(false);
    }
  }, [activeProfileId, switching, loadUserProfile, loadRecentScans]);

  const checkPaymentStatus = useCallback(async (sessionId) => {
    toast.info('Processing your upgrade...');
    try {
      const response = await axios.get(`${API_URL}/payments/checkout/status/${sessionId}`, {
        withCredentials: true
      });
      if (response.data.payment_status === 'paid') {
        toast.success('Welcome to Pro!');
        await loadProfile();
        await loadUserProfile();
        searchParams.delete('session_id');
        setSearchParams(searchParams);
      } else {
        toast.error('Payment is still processing. Please refresh.');
      }
    } catch (error) {
      console.error('[Home] Payment status check failed:', error);
      toast.error('Error verifying payment.');
    }
  }, [token, loadProfile, loadUserProfile, searchParams, setSearchParams]);

  useEffect(() => {
    loadUserProfile();
    loadRecentScans();
    loadProfileList();
  }, [loadUserProfile, loadRecentScans, loadProfileList]);

  // Check for push notification reminders when page loads
  useEffect(() => {
    if (profile && recentScans) {
      const hasScannedToday = recentScans.some(s => {
        const scanDate = new Date(s.timestamp);
        const today = new Date();
        return scanDate.toDateString() === today.toDateString();
      });
      checkStreakReminder(hasScannedToday, profile?.streak_days || 0);
    }
  }, [profile, recentScans]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) checkPaymentStatus(sessionId);
  }, [searchParams, checkPaymentStatus]);

  const remainingScans = profile?.plan === 'free'
    ? Math.max(0, (profile?.scans_limit || 7) - (profile?.scans_used || 0))
    : null;

  const stats = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const startOfWeek = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6).getTime();
    let today = 0, week = 0, gos = 0, hellnos = 0;
    for (const s of recentScans) {
      const t = new Date(s.timestamp).getTime();
      if (t >= startOfToday) today += 1;
      if (t >= startOfWeek) week += 1;
      if (s.decision === 'GO') gos += 1;
      else if (s.decision === 'HELL NO') hellnos += 1;
    }
    return { today, week, gos, hellnos };
  }, [recentScans]);

  const displayName = useMemo(() => {
    if (profile?.name) return profile.name;
    const email = profile?.email || '';
    const handle = email ? email.split('@')[0] : 'there';
    return handle.length > 14 ? handle.slice(0, 14) : handle;
  }, [profile]);

  const verdictColor = (d) => d === 'GO' ? 'text-qc-green' : d === 'MAYBE' ? 'text-qc-amber' : 'text-qc-red';
  const verdictBg = (d) => d === 'GO' ? 'bg-qc-green' : d === 'MAYBE' ? 'bg-qc-amber' : 'bg-qc-red';

  return (
    <div
      className="min-h-screen bg-qc-bg text-qc-text pb-28"
      data-testid="home-screen"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
    >
      {/* Header */}
      <div className="px-5 pt-2 pb-3 flex items-center justify-between max-w-lg mx-auto">
        <span className="text-2xl font-black text-qc-green tracking-tight" data-testid="home-brand">Green Light</span>
        <div className="flex items-center gap-2">
          {profile?.plan === 'pro' && (
            <div className="flex items-center gap-1 bg-qc-amber/15 text-qc-amber border border-qc-amber/30 px-3 py-1.5 rounded-full text-xs font-bold" data-testid="pro-badge">
              <Crown className="w-3.5 h-3.5" />
              Pro
            </div>
          )}
          <button
            onClick={() => navigate('/settings')}
            data-testid="settings-button"
            className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text-dim hover:text-qc-text tappable"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text-dim hover:text-qc-text tappable"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-5 pt-3">
        <ProfileSwitcher
          profiles={profileList}
          activeProfileId={activeProfileId}
          switching={switching}
          onSwitch={switchProfile}
          onManage={() => navigate('/profiles')}
        />

        {/* Greeting */}
        <div className="mb-5 animate-fade-up">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-qc-text leading-tight" data-testid="home-greeting">
            Hey {displayName} —<br />
            <span className="text-qc-text-dim">
              {profile?.is_kid ? "let's find you a snack." : 'ready to shop smarter?'}
            </span>
          </h1>
          {profile?.is_kid && (
            <span
              className="inline-flex items-center gap-1 mt-2 text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-full bg-qc-amber/15 text-qc-amber border border-qc-amber/30"
              data-testid="kid-mode-badge"
            >
              <Baby className="w-3 h-3" /> Kid mode
            </span>
          )}
        </div>

        {/* Streak banner — primary engagement surface */}
        <StreakBanner
          streak={profile?.streak_days || 0}
          longest={profile?.longest_streak || 0}
          totalScans={profile?.scans_used || 0}
        />

        {/* Evening streak reminder */}
        <StreakReminder
          hasScannedToday={stats.today > 0}
          streakDays={profile?.streak_days || 0}
          onScanNow={() => navigate('/scan')}
        />

        {/* Push notification prompt REMOVED - was causing mobile black screen */}

        {/* Quick Stats */}
        <div className="grid grid-cols-4 gap-2 mb-5" data-testid="quick-stats">
          <Stat label="Today" value={stats.today} accent="text-qc-text" />
          <Stat label="This week" value={stats.week} accent="text-qc-text" />
          <Stat label="GOs" value={profile?.total_gos ?? stats.gos} accent="text-qc-green" testid="stat-gos" />
          <Stat label="Hell No" value={profile?.total_hellnos ?? stats.hellnos} accent="text-qc-red" testid="stat-hellnos" />
        </div>

        {/* Scan Now button - primary CTA */}
        <button
          onClick={() => navigate('/scan')}
          disabled={profile?.plan === 'free' && remainingScans === 0}
          data-testid="scan-now-button"
          className="capture-btn w-full bg-qc-green text-qc-bg rounded-3xl py-6 mb-3 font-black text-xl tracking-tight flex items-center justify-center gap-3 shadow-[0_10px_40px_rgba(0,230,118,0.35)] disabled:opacity-30 disabled:cursor-not-allowed border-4 border-qc-green/40"
        >
          <Camera className="w-7 h-7" strokeWidth={2.5} />
          Scan Now
        </button>

        {/* Menu scan CTA — smaller secondary action */}
        <button
          onClick={() => navigate('/menu-scan')}
          disabled={profile?.plan === 'free' && remainingScans === 0}
          data-testid="menu-scan-cta"
          className="w-full bg-qc-surface border border-qc-border rounded-2xl py-3 mb-5 flex items-center justify-center gap-2 text-qc-text font-bold text-sm tappable disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <UtensilsCrossed className="w-4 h-4 text-qc-amber" strokeWidth={2.5} />
          Scan a restaurant menu
          {profileList.length > 1 && (
            <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-qc-amber/15 text-qc-amber ml-1">
              Whole table
            </span>
          )}
        </button>

        {/* Goal Reminder */}
        {profile?.goal && goalReminders[profile.goal] && (
          <div className="bg-qc-surface border border-qc-border rounded-2xl px-4 py-3 mb-5 flex items-start gap-3" data-testid="goal-reminder">
            <div className="w-2 h-2 mt-2 rounded-full bg-qc-green flex-shrink-0" />
            <p className="text-sm text-qc-text-dim leading-snug">
              <span className="text-qc-text font-bold">{LABELS.goal[profile.goal]}.</span>{' '}
              {goalReminders[profile.goal]}
            </p>
          </div>
        )}

        {/* Profile Summary Card */}
        {profile && (profile.goal || profile.eating_style || profile.shopping_for) && (
          <div className="bg-qc-surface border border-qc-border rounded-2xl p-4 mb-5" data-testid="profile-summary">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-qc-text-dim uppercase tracking-wider">Your profile</h3>
              <button
                onClick={() => navigate('/onboarding')}
                data-testid="edit-profile-button"
                className="text-xs font-bold text-qc-green hover:text-qc-green-dark flex items-center gap-1"
              >
                <Settings className="w-3.5 h-3.5" /> Edit
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.goal && (
                <Pill text={LABELS.goal[profile.goal]} />
              )}
              {profile.eating_style && profile.eating_style !== 'none' && (
                <Pill text={LABELS.eating_style[profile.eating_style]} />
              )}
              {profile.shopping_for && (
                <Pill text={`For ${LABELS.shopping_for[profile.shopping_for] || profile.shopping_for}`} />
              )}
              {Array.isArray(profile.allergies) && profile.allergies.filter(a => a !== 'none').slice(0, 2).map(a => (
                <Pill key={a} text={`No ${LABELS.allergies[a] || a}`} tone="red" />
              ))}
              {Array.isArray(profile.avoid_ingredients) && profile.avoid_ingredients.slice(0, 2).map(a => (
                <Pill key={a} text={`Avoid ${LABELS.avoid_ingredients[a] || a}`} tone="amber" />
              ))}
              {profile.strictness && (
                <Pill text={`${profile.strictness} verdicts`} tone="green" />
              )}
              {profile.medical_diet_type && profile.medical_diet_type !== 'none' && (
                <Pill
                  text={
                    profile.medical_diet_type === 'glp1' && profile.glp1_medication
                      ? `${GLP1_MED_LABELS[profile.glp1_medication] || 'GLP-1'}${profile.glp1_titration_stage === 'starting' ? ' · starting' : ''}`
                      : MEDICAL_LABELS_HOME[profile.medical_diet_type] || 'Medical'
                  }
                  tone="red"
                />
              )}
            </div>
          </div>
        )}

        {/* Profiles link when only one profile exists (invites family/kids mode) */}
        {profileList.length <= 1 && (
          <button
            onClick={() => navigate('/profiles')}
            data-testid="add-family-profile-link"
            className="w-full mb-5 bg-qc-surface border border-qc-border rounded-2xl p-4 flex items-center gap-3 tappable text-left"
          >
            <div className="w-10 h-10 rounded-full bg-qc-surface2 flex items-center justify-center text-qc-text-dim">
              <Users className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-extrabold text-qc-text">Shopping for family?</p>
              <p className="text-xs text-qc-text-dim mt-0.5">Add kid or partner profiles — same subscription.</p>
            </div>
            <Plus className="w-5 h-5 text-qc-green" strokeWidth={3} />
          </button>
        )}

        {/* Plan Status */}
        <div className="bg-qc-surface border border-qc-border rounded-2xl p-4 mb-5" data-testid="plan-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-1">Plan</p>
              <p className="text-lg font-extrabold text-qc-text capitalize">{profile?.plan || 'Free'}</p>
              {profile?.plan === 'free' && (
                <p className="text-sm text-qc-text-dim mt-0.5">
                  {remainingScans} of {profile?.scans_limit || 7} scans left
                </p>
              )}
              {profile?.plan === 'pro' && (
                <p className="text-sm text-qc-green font-bold mt-0.5">Unlimited scans</p>
              )}
            </div>
            {profile?.plan === 'free' && (
              <Button
                onClick={() => navigate('/upgrade')}
                data-testid="upgrade-button"
                className="h-11 bg-qc-amber hover:bg-qc-amber-dark text-qc-bg rounded-xl px-5 font-extrabold"
              >
                Upgrade
              </Button>
            )}
          </div>
        </div>

        {/* Recent scans */}
        {recentScans.length > 0 && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-qc-text-dim uppercase tracking-wider">Recent scans</h3>
              <button
                onClick={() => navigate('/history')}
                data-testid="view-all-history"
                className="text-xs font-bold text-qc-green hover:text-qc-green-dark"
              >
                View all
              </button>
            </div>
            <div className="space-y-2" data-testid="recent-scans-list">
              {recentScans.slice(0, 5).map((scan) => (
                <button
                  key={scan.id}
                  onClick={() => navigate(`/result/${scan.id}`)}
                  data-testid={`recent-scan-${scan.id}`}
                  className="w-full bg-qc-surface border border-qc-border rounded-2xl p-3 flex items-center gap-3 tappable text-left"
                >
                  <div className="w-14 h-14 rounded-xl bg-qc-surface2 overflow-hidden flex-shrink-0 relative">
                    {scan.thumbnail_url ? (
                      <img src={scan.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-qc-text-faint text-xs">
                        no img
                      </div>
                    )}
                    <span className={`absolute -top-1 -right-1 w-4 h-4 rounded-full ${verdictBg(scan.decision)} border-2 border-qc-bg`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-qc-text truncate text-base">{scan.item_label || 'Older scan'}</p>
                    <p className={`text-xs font-black uppercase tracking-wider mt-0.5 ${verdictColor(scan.decision)}`}>
                      {scan.decision}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Scan limit warning */}
        {profile?.plan === 'free' && remainingScans !== null && remainingScans <= 2 && (
          <div
            className="bg-qc-amber/10 border border-qc-amber/40 rounded-2xl p-4 text-center mb-2"
            data-testid="scan-limit-warning"
          >
            <p className="text-qc-amber font-bold text-sm">
              {remainingScans === 0
                ? 'No scans remaining. Upgrade to keep going.'
                : `Only ${remainingScans} scan${remainingScans === 1 ? '' : 's'} left`}
            </p>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-qc-bg/95 backdrop-blur-lg border-t border-qc-border flex justify-around items-center z-40"
        style={{ paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))', paddingTop: '0.5rem' }}
      >
        <NavBtn icon={Camera} label="Scan" active onClick={() => navigate('/scan')} testid="nav-scan" />
        <NavBtn icon={Heart} label="Favorites" onClick={() => navigate('/favorites')} testid="nav-favorites" />
        <NavBtn icon={Clock} label="History" onClick={() => navigate('/history')} testid="nav-history" />
      </div>
    </div>
  );
}

function Stat({ label, value, accent = 'text-qc-text', testid }) {
  return (
    <div className="bg-qc-surface border border-qc-border rounded-2xl px-2 py-3 text-center" data-testid={testid}>
      <div className={`text-2xl font-black ${accent} tabular-nums`}>{value}</div>
      <div className="text-[10px] font-bold text-qc-text-dim uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function Pill({ text, tone = 'neutral' }) {
  const toneCls = {
    neutral: 'bg-qc-surface2 border-qc-border text-qc-text',
    green: 'bg-qc-green/10 border-qc-green/30 text-qc-green',
    amber: 'bg-qc-amber/10 border-qc-amber/30 text-qc-amber',
    red: 'bg-qc-red/10 border-qc-red/30 text-qc-red'
  }[tone];
  return (
    <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${toneCls} capitalize`}>
      {text}
    </span>
  );
}

function NavBtn({ icon: Icon, label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex flex-col items-center gap-1 px-6 py-2 ${active ? 'text-qc-green' : 'text-qc-text-faint hover:text-qc-text-dim'} transition-colors`}
    >
      <Icon className="w-6 h-6" />
      <span className="text-[11px] font-bold">{label}</span>
    </button>
  );
}
